import { useT } from "../i18n";
import { Knopf } from ".";
import { Deckblatt } from "./Deckblatt";
import { tagMitWochentag } from "./preistext";
import { merksperreSchliessen, useMerksperre } from "../daten/merkliste";
import type { Merksperre as Sperre } from "../daten/merkliste";
import "../stile/merksperre.css";

/* ══════════════════════════════════════════════════════════════════
   Das Fenster, wenn etwas nicht in die Sammlung darf.

   Luca, 19.09.2026: *„Es muss eine Fehlermeldung kommen, sobald man das
   Ding merken will, das darf ja nicht gehen. Richtiges Popup, sowohl auf
   Handy als auf Desktop."*

   **Ein Deckblatt, kompakt** — am Schreibtisch in der Mitte über dem
   Arbeitsbereich, am Telefon als Blatt von unten. Dieselbe Form wie jede
   andere kurze Auskunft, mit Fokusfalle, `Esc` und Zurückgeste. Kein
   Ruf unten rechts: Der verschwindet von selbst, und genau das darf eine
   Absage nicht — sonst glaubt man, es sei gemerkt.

   **Es steht einmal in der Schale** und nicht an jeder der sechs Stellen,
   an denen gemerkt wird: Die Prüfung legt den Befund in einen Zustand
   (`daten/merkliste.ts`, `merksperreZeigen`), und dieses Fenster liest ihn.

   **Es sagt, was nicht ging und warum** — Gerät, Zeitraum, wie viele frei
   sind und ab wann wieder. Und es sagt ausdrücklich: nicht gemerkt.
   ══════════════════════════════════════════════════════════════════ */

function Grund({ s }: { s: Sperre }) {
  const t = useT();
  if (s.nurMitTraeger) return <>{t("merksperre.nurMitTraeger")}</>;
  if (s.bestand === 0) return <>{t("merksperre.keinBestand")}</>;
  if (s.frei > 0) {
    return (
      <>
        {t(s.schon > 0 ? "merksperre.zuWenigMitSammlung" : "merksperre.zuWenig", {
          frei: s.frei, braucht: s.braucht, schon: s.schon,
        })}
      </>
    );
  }
  return (
    <>
      {s.freiAb
        ? t("merksperre.erstAb", { tag: tagMitWochentag(t, s.freiAb) })
        : t("merksperre.nichtsFrei")}
    </>
  );
}

export function Merksperre() {
  const t = useT();
  const sperren = useMerksperre();
  const offen = sperren.length > 0;
  /* **Ein Anbauteil ohne Träger ist keine Frage der Lage** (Befund 44,
     25.09.2026). „Nicht frei" wäre dort falsch — die Schaufel kann frei
     sein und geht trotzdem nicht allein hinaus. Stehen nur solche da,
     sagen Titel und Weg das; stehen beide Sorten da, stehen beide Wege. */
  const traeger = sperren.filter((s) => s.nurMitTraeger).length;
  const nurTraeger = offen && traeger === sperren.length;
  const titel = nurTraeger
    ? "merksperre.titelTraeger"
    : sperren.length > 1 ? "merksperre.titelMehrere" : "merksperre.titel";

  return (
    <Deckblatt
      offen={offen}
      kompakt
      weite={520}
      schliessen={merksperreSchliessen}
      titel={t(titel)}
      untertitel={t(nurTraeger ? "merksperre.unterTraeger" : "merksperre.unter")}
    >
      <div className="msp">
        <ul className="msp-liste">
          {sperren.map((s, i) => (
            <li key={`${s.name}-${s.von}-${i}`} className="msp-zeile">
              <b>{s.name}</b>
              {/* Der Zeitraum sagt bei einem Anbauteil nichts — es ginge in
                  keinem allein hinaus. */}
              {s.nurMitTraeger ? null : (
                <span className="msp-zeit">
                  {t("merksperre.zeitraum", {
                    von: tagMitWochentag(t, s.von),
                    bis: tagMitWochentag(t, s.bis),
                  })}
                </span>
              )}
              <span className="msp-grund"><Grund s={s} /></span>
            </li>
          ))}
        </ul>
        {nurTraeger ? null : <p className="msp-satz">{t("merksperre.weiter")}</p>}
        {traeger ? <p className="msp-satz">{t("merksperre.weiterTraeger")}</p> : null}
        <div className="msp-fuss">
          <Knopf art="haupt" autoFocus onClick={merksperreSchliessen}>{t("merksperre.verstanden")}</Knopf>
        </div>
      </div>
    </Deckblatt>
  );
}
